<script setup lang="ts">
import type {
  Image,
  UIMediaProps,
} from '@design-system/components/UI/UIMedia.props'
import type {
  AlgoliaData,
  AlgoliaImages,
} from '../Templates/TemplatesPlpShopTheLook.type'

const {
  refineAndUpdateState,
  previousCanonicalPage,
  nextCanonicalPage,
  setInfiniteScroll,
  setInfiniteScroll2Page,
  infScroll,
  state,
  loadMoreButton,
} = useAlgoliaAsInfiniteHits()

const { getProducts } = useShopTheLook()

const shopTheLookModalRef = ref()

const getClass = (index: number) => {
  const cycleIndex = index % 10
  return cycleIndex < 6
    ? 'col-span-6 md:col-span-4'
    : 'col-span-full md:col-span-6'
}

const getPrimaryImage = (algoliaData: AlgoliaData): Image => {
  const filtered = algoliaData.assets.filter(
    (image: AlgoliaImages) => image.priority === '1'
  )
  return {
    type: 'image',
    srcImage: {
      secure_url: filtered[0].URL,
      public_id: filtered[0].URL,
    },
    alt: algoliaData.LookName,
  }
}

const getShopTheLookMainImage = (algoliaData: AlgoliaData): UIMediaProps => {
  const filtered = algoliaData.assets.filter(
    (image: AlgoliaImages) => image.priority === '1'
  )
  const hoverMedia: Image = {
    type: 'image',
    srcImage: {
      secure_url: filtered[0].URL,
      public_id: filtered[0].URL,
    },
    alt: algoliaData.LookName,
  }
  return { media: hoverMedia, aspectRatio: '4/5' }
}

const getShopTheLookHoverImage = (
  algoliaData: AlgoliaData
): UIMediaProps | undefined => {
  const filtered = algoliaData.assets.filter(
    (image: AlgoliaImages) => image.priority === '2'
  )
  if (filtered.length) {
    const hoverMedia: Image = {
      type: 'image',
      srcImage: {
        secure_url: filtered[0].URL,
        public_id: filtered[0].URL,
      },
      alt: algoliaData.LookName,
    }

    return { media: hoverMedia, aspectRatio: '4/5' }
  } else {
    return undefined
  }
}

const openShopTheLook = async (index: number, products: string[]) => {
  if (shopTheLookModalRef.value && products?.length) {
    shopTheLookModalRef.value[index].openDialog(
      shopTheLookModalRef.value[index].uniqueShopTheLookModalId
    )
  }
}
</script>

<template>
  <AisInfiniteHits>
    <template #loadPrevious="{ isFirstPage, refinePrevious }">
      <div>
        <div
          v-if="!isFirstPage"
          class="flex items-center justify-center py-8 md:py-12 lg:py-16"
        >
          <a
            class="cta cta-l cta-primary"
            :href="previousCanonicalPage"
            @click.prevent="refineAndUpdateState(refinePrevious, true)"
          >
            {{ $ts('plp.showLess') }}
          </a>
        </div>
      </div>
    </template>
    <template #default="{ isLastPage, refineNext, items }">
      <div class="grid grid-cols-12">
        <UIMedia
          v-for="(item, index) in items"
          ref="shopTheLookModalRef"
          :key="index"
          :media="getPrimaryImage(item as unknown as AlgoliaData)"
          :shop-the-look="{
            id: 'shopTheLook',
            title: item.LookName as string,
            subtitle: (item.LookText as string) ?? null,
            products: item.Products as string[],
            media: getShopTheLookMainImage(item as unknown as AlgoliaData),
            hoverMedia: getShopTheLookHoverImage(
              item as unknown as AlgoliaData
            ),
            iconShopTheLookColor: 'black',
          }"
          :class="getClass(index)"
          class="h-full w-full cursor-pointer"
          @click="openShopTheLook(index, (item.Products as string[]) ?? [])"
        />
      </div>

      <AisStats>
        <template #default="{ nbHits }">
          <div>
            <div id="loadMoreStrip" class="h-2 w-full"></div>

            <div
              v-if="!isLastPage"
              class="flex flex-col items-center justify-center py-8 md:py-12 lg:py-16"
            >
              <a
                ref="loadMoreButton"
                :class="[
                  {
                    hidden: infScroll,
                  },
                ]"
                :href="nextCanonicalPage"
                class="cta cta-l cta-primary text-button-2"
                role="button"
                @click.prevent="refineAndUpdateState(refineNext)"
              >
                {{ `${$ts('plp.showMore')} (${state.hits?.length}/${nbHits})` }}
              </a>

              <AtomsCta
                v-if="!infScroll"
                :icon-show-default="false"
                anatomy="ghost"
                @click-handler="
                  nbHits / state.hits?.length > 2
                    ? setInfiniteScroll()
                    : setInfiniteScroll2Page()
                "
              >
                <template #label>
                  {{ $ts('plp.showAll') }}
                </template>
              </AtomsCta>
              <div id="loadMoreButton" class="h-[100%] w-[100vw]"></div>
            </div>
          </div>
        </template>
      </AisStats>
    </template>
  </AisInfiniteHits>
</template>
